import DataTableColumnHeader from '@/components/datatable/DataTableColumnHeader.vue';
import VatTableActions from '@/components/vat/VatTableActions.vue';
import VatTableInput from '@/components/vat/VatTableInput.vue';
import VatTableTotalInput from '@/components/vat/VatTableTotalInput.vue';
import type { VatSubmission } from '@/types';
import type { CellContext, Column, ColumnDef, ColumnMeta } from '@tanstack/vue-table';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const columnHelper = createColumnHelper<VatSubmission>();

const createEditableCell = (
    accessorKey: keyof VatSubmission,
    withInfo: boolean = false,
    border: boolean = false,
) => ({
    accessorKey,
    header: ({ column }: { column: Column<VatSubmission, unknown> }) =>
        h(DataTableColumnHeader, {
            column: column,
            title: `vat.columns.${accessorKey}`,
            info: withInfo ? `vat.column_helpers.${accessorKey}` : undefined,
        }),
    cell: (ctx: CellContext<VatSubmission, any>) =>
        h(VatTableInput, {
            modelValue: ctx.getValue(),
            table: ctx.table,
            row: ctx.row,
            columnId: accessorKey,
        }),
    meta: {
        filterType: 'currency' as ColumnMeta<VatSubmission, any>['filterType'],
        border: border,
    },
});

export const columns: ColumnDef<VatSubmission>[] = [
    columnHelper.group({
        header: '',
        id: 'company',
        columns: [
            {
                id: 'company_name',
                accessorKey: 'company_name',
                header: ({ column }) =>
                    h(DataTableColumnHeader, {
                        column: column,
                        title: 'vat.columns.company',
                    }),
                enableHiding: false,
            },
            {
                accessorKey: 'company_group',
                header: ({ column }) =>
                    h(DataTableColumnHeader, {
                        column: column,
                        title: 'vat.columns.company_group',
                    }),
                filterFn: 'arrIncludesSome',
                meta: {
                    filterType: 'checkbox',
                },
            },
            {
                id: 'period',
                accessorKey: 'period',
                header: ({ column }) =>
                    h(DataTableColumnHeader, {
                        column: column,
                        title: 'vat.columns.period',
                    }),
                filterFn: 'arrIncludesSome',
                meta: {
                    filterType: 'checkbox',
                },
            },
        ],
    }),
    columnHelper.group({
        id: 'danish_sales_and_purchase_vat',
        header: ({ column }) =>
            h(DataTableColumnHeader, {
                column: column,
                title: 'vat.group_headers.danish_sales_and_purchase_vat',
            }),
        meta: {
            border: true,
        },
        columns: [createEditableCell('sales_vat'), createEditableCell('purchase_vat', false, true)],
    }),
    columnHelper.group({
        id: 'trade_with_abroad',
        header: ({ column }) =>
            h(DataTableColumnHeader, {
                column: column,
                title: 'vat.group_headers.trade_with_abroad',
            }),
        meta: {
            border: true,
        },
        columns: [
            createEditableCell('vat_on_purchase_of_goods_abroad', true),
            createEditableCell('vat_on_purchase_of_services_abroad'),
            createEditableCell('box_a_goods', true),
            createEditableCell('box_a_services', true),
            createEditableCell('box_b_goods_reported', true),
            createEditableCell('box_b_goods_not_reported', true),
            createEditableCell('box_b_services', true),
            createEditableCell('box_c', false, true),
        ],
    }),
    columnHelper.group({
        id: 'energy_taxes',
        header: ({ column }) =>
            h(DataTableColumnHeader, {
                column: column,
                title: 'vat.group_headers.energy_taxes',
            }),
        meta: {
            border: true,
        },
        columns: [
            createEditableCell('oil_and_bottled_gas_tax'),
            createEditableCell('electricity_tax'),
            createEditableCell('natural_gas_and_town_gas_tax'),
            createEditableCell('coal_tax'),
            createEditableCell('co2_tax'),
            createEditableCell('water_tax', false, true),
        ],
    }),
    columnHelper.group({
        id: 'vat_total',
        header: ({ column }) =>
            h(DataTableColumnHeader, {
                column: column,
                title: 'vat.group_headers.vat_total',
            }),
        meta: {
            border: true,
        },
        columns: [
            {
                accessorKey: 'total_vat',
                header: ({ column }) =>
                    h(DataTableColumnHeader, {
                        column: column,
                        title: 'vat.columns.total_vat',
                    }),
                cell: (ctx: CellContext<VatSubmission, any>) =>
                    h(VatTableTotalInput, {
                        row: ctx.row,
                        table: ctx.table,
                        columnId: 'total_vat',
                    }),
                meta: {
                    filterType: 'currency',
                    border: true,
                },
            },
        ],
    }),
    columnHelper.group({
        id: 'actions_group',
        header: '',
        columns: [
            {
                id: 'actions',
                enableHiding: false,
                header: ({ column }) =>
                    h(DataTableColumnHeader, {
                        column: column,
                        title: 'tables.actions',
                    }),
                cell: ({ row, table }) => {
                    return h(VatTableActions, {
                        vatSubmission: row.original,
                        row: row,
                        table: table,
                    });
                },
            },
        ],
    }),
];
